import { FC, ReactElement, useEffect, useId, useRef, useState } from 'react';

import { Button, ButtonVariant } from '@pp/core/src/components/controls/button/Button';
import { CurrencyConnected } from '@pp/core/src/components/currency/CurrencyConnected';
import { Loader } from '@pp/core/src/components/loader/index';
import { L10nText } from '@pp/core/src/services/uiTranslations/reactBindings/L10nText';
import { useAudioTrigger } from '@pp/core/src/controllers/audioController/hooks';
import { useGameStateSlice } from '@pp/core/src/store/slices/game/selectors';
import { GameStatus } from '@pp/core/src/store/slices/game/types';
import { useSettingsSlice } from '@pp/core/src/store/slices/settings/selectors';
import { useModifiers } from '@pp/core/src/styles/hooks/useModifiers';
import { getIsLanguageRTL } from '@pp/core/src/utils/language';

import { trimmedTime, truncateToTwoDecimal } from '../../../config/utils';
import { useInGameHistoryDataFetcher } from '../../../store/apiHooks/useInGameHistoryDataFetcher';
import { useGameDispatch } from '../../../store/GameStore';
import { useInGameHistorySlice } from '../../../store/slices/inGameHistory/selectors';
import { inGameHistoryActions } from '../../../store/slices/inGameHistory/slice';
import { useCurrencySymbol } from '../../gameGrid/hooks';
import { ErrorMessage } from '../errorMessage/ErrorMessage';
import { ScrollableWrapper } from '../scrollableWrapper/ScrollableWrapper';
import { SingleMultiplier } from '../singleMultiplier/SingleMultiplier';

import styles from './InGameHistoryConnected.module.css';

export const InGameHistoryConnected: FC = () => {
  const id = useId();
  const loadedRef = useRef(false);
  const dispatch = useGameDispatch();
  const audioTrigger = useAudioTrigger();
  const currencySymbol = useCurrencySymbol();
  const locale = useSettingsSlice.currentLanguage();
  const isLanguageRTL = getIsLanguageRTL(locale);
  const currency = useGameStateSlice.getCurrency() ?? '';
  const currentLanguage = useSettingsSlice.currentLanguage();
  const isApiRejected = useInGameHistorySlice.getIsApiRejected();
  const inGameHistoryDataFetcher = useInGameHistoryDataFetcher();
  const inGameHistoryData = useInGameHistorySlice.getInGameHistory();
  const currentPageNumber = Math.round(inGameHistoryData.ids.length / 20);
  const [page, setPage] = useState<number>(currentPageNumber ? currentPageNumber : 1);
  const isGameStarted = useGameStateSlice.getCurrentStatus() === GameStatus.PlaceYourBets;

  const mods = useModifiers(
    styles,
    'inGameHistory_header_row',
    {
      small: currency === 'mBTC' || ['bn', 'hi', 'ar'].includes(currentLanguage),
      rtl: isLanguageRTL,
    },
    { withBaseClass: true }
  );

  useEffect(() => {
    if (!isApiRejected && !inGameHistoryData.loading) {
      loadedRef.current = true;
    } else {
      loadedRef.current = false;
    }
  }, [isApiRejected, inGameHistoryData.loading]);

  useEffect(() => {
    inGameHistoryDataFetcher(page, true);
  }, []);

  useEffect(() => {
    if (!isApiRejected && isGameStarted) {
      inGameHistoryDataFetcher(page, true);
    }
  }, [isApiRejected, isGameStarted]);

  return (
    <ScrollableWrapper>
      {((): ReactElement => {
        if (isApiRejected && !inGameHistoryData.ids.length) {
          return (
            <ErrorMessage
              title="HIGHFLYER_FAILED_TO_LOAD"
              message="HIGHFLYER_INTERNAL_ERROR"
              disabled={false}
              loading={false}
              onClick={(): void => {
                dispatch(inGameHistoryActions.setApiRejected());
                inGameHistoryDataFetcher(page, true);
              }}
            />
          );
        } else if (!isApiRejected && !inGameHistoryData.ids.length && inGameHistoryData.loading && !loadedRef.current) {
          return <Loader />;
        } else {
          return (
            <>
              <table className={styles.inGameHistory_table}>
                <div className={styles.header_border}></div>
                <thead className={styles.inGameHistory_header}>
                  <tr className={mods}>
                    <td className={styles.firstCol}>
                      <L10nText tk="TIME" />
                    </td>
                    <td className={styles.secondCol}>
                      <L10nText tk="BETLABEL" />
                      <span>&nbsp;{currencySymbol}</span>
                    </td>
                    <td className={styles.thirdCol}></td>
                    <td className={styles.fourthCol}>
                      <L10nText tk="CASHED" />
                      <span>&nbsp;{currencySymbol}</span>
                    </td>
                  </tr>
                </thead>
                <tbody className={styles.inGameHistory_body}>
                  {inGameHistoryData.ids.map((uid: number | string) => {
                    return (
                      <tr
                        key={`in-game-history-${id}`}
                        className={`${styles.inGameHistory_body_row} ${Number(inGameHistoryData.entities?.[uid]?.winAmount) > 0 && Number(inGameHistoryData.entities?.[uid]?.cashoutMul) > 0 && styles.inGameHistory_body_row_active}`}
                      >
                        <td className={styles.firstCol}>
                          {trimmedTime(inGameHistoryData.entities?.[uid]?.placeBetTime ?? '')}
                        </td>
                        <td className={styles.secondCol}>
                          <CurrencyConnected
                            value={Number(inGameHistoryData.entities?.[uid]?.betAmount)}
                            hideCurrency
                          />
                        </td>
                        <td
                          /* style={{
                            cursor:
                              Number(inGameHistoryData.entities?.[uid]?.cashoutMul) > 0 ||
                              inGameHistoryData.entities?.[uid]?.isGameCancelled
                                ? 'auto'
                                : 'pointer',
                          }} */
                          className={styles.thirdCol}
                         /*  onClick={
                            Number(inGameHistoryData.entities?.[uid]?.cashoutMul) < 0
                              ? (): void => {
                                audioTrigger.play({ name: 'menuClick' });
                                openHashModel(
                                  Number(inGameHistoryData.entities?.[uid]?.uId),
                                  'history',
                                  Number(inGameHistoryData.entities?.[uid]?.crashMul),
                                  inGameHistoryData.entities?.[uid]?.hashKey ?? '',
                                  inGameHistoryData.entities?.[uid]?.encryptedResult ?? '',
                                  inGameHistoryData.entities?.[uid]?.placeBetDate.concat(
                                    ' ',
                                    inGameHistoryData.entities?.[uid]?.placeBetTime ?? ''
                                  ) ?? ''
                                );
                              }
                              : undefined
                          } */
                        >
                          {!inGameHistoryData.entities?.[uid]?.isGameCancelled && (
                            <SingleMultiplier
                              value={
                                Number(inGameHistoryData.entities?.[uid]?.cashoutMul) > 0
                                  ? Number(inGameHistoryData.entities?.[uid]?.cashoutMul)
                                  : Number(inGameHistoryData.entities?.[uid]?.crashMul)
                              }
                              plain={Number(inGameHistoryData.entities?.[uid]?.cashoutMul) <= 0}
                            />
                          )}
                        </td>
                        <td
                          className={`${styles.fourthCol} ${Number(inGameHistoryData.entities?.[uid]?.winAmount) > 0 && Number(inGameHistoryData.entities?.[uid]?.cashoutMul) > 0 && styles.fourthCol_won}`}
                        >
                          <CurrencyConnected
                            value={truncateToTwoDecimal(Number(inGameHistoryData.entities?.[uid]?.winAmount))}
                            hideCurrency
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {inGameHistoryData.ids.length > 0 && inGameHistoryData.loadMore && (
                <div className={styles.loadMore}>
                  <Button
                    tk="LOAD_MORE"
                    variant={ButtonVariant.MAIN}
                    disabled={!inGameHistoryData.loadMore}
                    loading={inGameHistoryData.loading}
                    onClick={() => {
                      setPage((prev) => prev + 1);
                      audioTrigger.play({ name: 'menuClick' });
                      inGameHistoryDataFetcher(page + 1, true);
                    }}
                    className={styles.buttonBg}
                  ></Button>
                </div>
              )}
            </>
          );
        }
      })()}
    </ScrollableWrapper>
  );
};
